import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FilterBar, ModalVariables, SearchComponent, StartPage } from "../../../components";
import { useAuth } from "../../../context/user.context";
import { icons } from "../../../icons";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { useNavigate } from "react-router-dom";
import { useUi } from "../../../context/ui.context";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { clientService } from "../../../api";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { compareArrays } from "../../../utils/findIntersection";
import { usePageTitle } from "../../../context/navigationContext";
const USER_FILTER_KEYS = [
  'date_joined',
  'last_login',
  'is_active',
  'is_reseller',
  'is_staff',
  'is_superuser',
  'user_groups',
];
const DEFAULT_ORDERING = '-date_joined';

const Users:FC = () => {
  const {user, updateUser} = useAuth()
  const [rows, setRows] = useState<any[]>([])
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<any>()
  
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam)
  const [searchString, setSearchString] = useQueryParam('search', StringParam)
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam)
  const {viewAlert} = useUi()
  const [paginationData, setPaginationData] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any[]>([])
  const [t] = useTranslation()
  usePageTitle(t("PAGE_TITLE_USERS"));
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [filterOptions, setFilterOptions] = useState<any>()
  const [userGroupOptions, setUserGroupOptions] = useState<any[]>([])
  const normalizedFilterOptions = useMemo(() => {
    const base: Record<string, any> = {};
    if (filterOptions) {
      USER_FILTER_KEYS.forEach((key) => {
        if (Array.isArray(filterOptions?.[key]) && filterOptions?.[key]?.length) {
          base[key] = filterOptions[key];
        }
      });
    }
    const booleanOptions = [
      { display: t("YES"), value: 'true' },
      { display: t("NO"), value: 'false' },
    ];
    ['is_active', 'is_reseller', 'is_staff', 'is_superuser'].forEach((key) => {
      if (!base[key]) {
        base[key] = booleanOptions;
      }
    });
    // Add user_groups filter with remote search capability
    // If we have pre-loaded options use them, otherwise use empty array
    // The FilterBar will handle remote search for user_groups
    if (!base.user_groups) {
      base.user_groups = userGroupOptions.length ? userGroupOptions : [];
    }
    return base;
  }, [filterOptions, t, userGroupOptions]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      setAllChecked(false)
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(rows, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  const updateList = async () => {
    try{
      const res = await clientService.getUsers({
        page:page||1,
        ordering: ordering || DEFAULT_ORDERING,
        pageSize:pageSize||10,
        search:searchString||'',
        filtering: filtering || '',
      })
      let data = res.data.objects.map((user:any) => {
        const labels = [];
        if (user.is_staff) labels.push('staff');
        if (user.is_superuser) labels.push('superuser');
        if (user.is_reseller) labels.push('reseller');
        if (!user.is_active) labels.push('inactive');

        return {
        id: user.id,
        navigate: `${user.id}`,
        full_name: user.full_name || `${user.first_name} ${user.last_name}`,
        email: user.email,
        date_created: timeConverterMMDDYYHHMM(user.date_joined),
        last_login: user.last_login ? timeConverterMMDDYYHHMM(user.last_login) : t("NEVER"),
        labels: labels.join(', '),
        avatar_email: user.email,
        status_active: user.is_active,
        is_staff: user.is_staff,
        is_superuser: user.is_superuser,
        is_reseller: user.is_reseller,
        is_active: user.is_active,
        sfa_enabled: user.sfa_enabled,
        first_name: user.first_name,
        last_name: user.last_name
      }})
      setPaginationData(res.data)
      setFilterOptions(res.data?.filter_options || {});
      setRows(data)
      setPaginationData(res.data);
      setPageLoading(false)

    } catch(e:any){
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
      setPageLoading(true)

    }
    
  }
  const navigate = useNavigate()
  const canDissociate = (id:any) => {
    
  }

  useEffect(() => {
    updateList()
  },[page, ordering, pageSize, searchString, filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  useEffect(() => {
    // fetch user groups once for filter fallback
    clientService.getUserGroups({
      page: 1,
      pageSize: 50,
      ordering: 'name',
      search: ''
    }).then((res:any) => {
      const opts = res?.data?.objects?.map((g:any) => ({
        display: g.name || g.id,
        value: g.id,
      })) || [];
      setUserGroupOptions(opts);
    }).catch(() => {
      setUserGroupOptions([]);
    });
  }, []);
  const prepareDeleteUsers = (items:any[]) => {
    return items.map((item:any) => ({
      ...item,
      name: item.full_name || item.email || item.id,
    }))
  }
  const openDeleteModal = (items:any[]) => {
    if (!items.length) return
    setModalType('delete')
    setAlertTitle(items.length === 1 ? t("USER") : t("USERS"))
    setAlertSubTitle(prepareDeleteUsers(items))
    setViewModal(true)
    setActiveAnimation(true)
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
    setSelectedUser(undefined)
  }
  const items = [
    {
      title:t("EDIT"),
      id:'edit',
      icon:icons.Edit,
      onClick: (item:any) => navigate(`${item.id}/edit`),
      divider:false
    },
    // {
    //   title:t("IMPERSONATE_USER"),
    //   id:'impersonate',
    //   icon:icons.User,
    //   onClick:(item:any) => {
    //     if (!item.is_superuser && !item.is_staff && item.is_active) {
    //       navigate(`${item.id}/impersonate`)
    //     }
    //   },
    //   disabled: (item:any) => item.is_superuser || item.is_staff || !item.is_active,
    //   divider:false
    // },
    // {
    //   title:t("DISABLE_SFA"),
    //   id:'disable-sfa',
    //   icon:icons.Lock,
    //   onClick:(item:any) => {
    //     // Implement disable SFA logic
    //     console.log('Disable SFA for', item.id);
    //   },
    //   disabled: (item:any) => !item.sfa_enabled,
    //   divider:false
    // },
    {
      title:t("DELETE"),
      id:'delete',
      onClick:(item:any) => {
        setSelectedUser(item);
        openDeleteModal([item])
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]

  const columns = [
    {
      title:t("ID"),
      key:"id",
      sorted:true,
    },
    {
      title:t("FULL_NAME"),
      key:'full_name',
      sorted:true,
    },
    {
      title:t("EMAIL"),
      key:"email",
      sorted:true,
    },
    {
      title:t("LAST_LOGIN"),
      key:"last_login",
      sorted:true,
    },
    {
      title:t("CREATED_AT"),
      key:"date_created",
      sorted:true,
    },
    {
      title:t("USER_LABELS"),
      key:"labels"
    },
    {
      title:"",
      key:'props'
    }
  ]
  const closeModal = async () => {
    setLoading(true)
    try{
      if (selectedUser) {
        await clientService.deleteUser(selectedUser.id)
        viewAlert({
          severity:'success',
          message:t("USER_DELETED_SUCCESSFULLY")
        })
        await updateList()
        setChecked([])
        setAllChecked(false)
      } else if(allChecked) {
        let promises:any[] = []
        await rows.map((item:any) => {
          let promise = clientService.deleteUser(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
        viewAlert({
          severity:'success',
          message:t("USERS_DELETED_SUCCESSFULLY")
        })
        await updateList()
        await setAllChecked(false)
        await setChecked([])
      } else if (checked?.length > 0){
        let promises:any[] = []
        await checked.map((item:any) => {
          let promise = clientService.deleteUser(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
        viewAlert({
          severity:'success',
          message:t("USERS_DELETED_SUCCESSFULLY")
        })
        await updateList()
        await setChecked([])
      }
    } catch (e:any) {
      if(e?.response?.data?.detail){
        viewAlert({
          severity:'error',
          message:e?.response?.data?.detail
        })
      }
    } finally {
      setViewModal(false)
      setActiveAnimation(false)
      setSelectedUser(undefined)
      setLoading(false)
    }
  }
  return(
    <>
      <StartPage
        title={`${t("USERS")}`}
        noResourceText={`${t("NO_USERS")}`}
        createResourceButton={`${t('CREATE_NEW_USER')}`}
        createResourceLink="/clients-users/users/create"
        haveResource={rows.length > 0}
      withoutCreateResourceButton
      rightCustomCreateButton={
        <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
          <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
            <Button
              title={t('CREATE_NEW_USER')}
              onClick={() => navigate('/clients-users/users/create')}
              variant='primary'
              size='custom'
              customStyles={{height:'40px', whiteSpace:'nowrap'}}
            />
            <div style={{flex:1, minWidth:'200px'}}>
              <SearchComponent />
            </div>
            <Button
              onClick={() => {
                openDeleteModal(allChecked ? rows : checked)
              }}
              variant='icon'
              size='display'
              test_id='button-delete'
              disabled={!allChecked && checked?.length === 0 }
            >
              <img src={icons.DeleteIcon}/>
            </Button>
            <Button
              title={t("CLEAR_FILTERS")}
              onClick={handleResetFilters}
              variant='stroke'
              size='display'
              disabled={
                (!filtering || filtering === '') &&
                !searchString &&
                ordering === DEFAULT_ORDERING
              }
            />
          </div>
          {normalizedFilterOptions ? (
            <FilterBar filterOptions={normalizedFilterOptions} />
          ) : null}
        </div>
      }
      pageLoading={pageLoading}
      tableData={{
        columns:columns,
        data:rows,
          props:items,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked,
          paginationData:paginationData,
          updateData:updateList
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
      />
    </>
  )
}

export default Users
